/**
 * Utilitário para gerar operações de patch comparando objetos
 * Usado para atualizar tasks via endpoint PATCH
 */

import { compare } from 'fast-json-patch';

/**
 * Compara dois objetos e retorna um array de operações de patch (RFC 6902).
 * A biblioteca lida com objetos aninhados, arrays e todos os tipos de mudanças.
 *
 * @param {Object} originalDoc - O documento JSON original.
 * @param {Object} modifiedDoc - O documento JSON modificado.
 * @returns {Array} Um array de operações de patch, ou um array vazio se não houver diferenças.
 */
export function generatePatchOperations(originalDoc, modifiedDoc) {
  // A biblioteca `fast-json-patch` é altamente otimizada para esta tarefa.
  return compare(originalDoc, modifiedDoc);
}

/**
 * Gera operações de PATCH otimizadas.
 * Para arrays como finalAttendanceDocList, faz replace completo em vez de operações item por item.
 * Para objetos como stage dentro de scheduleTypes, também faz replace completo.
 * Isso evita payloads complexos e instáveis.
 */
export const generateAndFormatPatchOperations = (original, updated) => {
  const operations = [];

  // Para finalAttendanceDocList: sempre replace completo do array
  // Evita payloads complexos com múltiplas operações remove/add
  if (updated.finalAttendanceDocList !== undefined) {
    operations.push({
      op: 'replace',
      path: '/FinalAttendanceDocList',
      value: updated.finalAttendanceDocList,
    });
  }

  // Detectar mudanças no stage de scheduleTypes (MainInstructor, Driver, etc.)
  const scheduleTypes = ['mainInstructor', 'driver', 'mainDriver'];

  scheduleTypes.forEach((scheduleType) => {
    if (original[scheduleType] && updated[scheduleType]) {
      // Verificar se o stage mudou
      const originalStage = original[scheduleType].stage;
      const updatedStage = updated[scheduleType].stage;

      if (originalStage && updatedStage && originalStage.id !== updatedStage.id) {
        // Capitalizar o scheduleType para o path
        const capitalizedScheduleType =
          scheduleType.charAt(0).toUpperCase() + scheduleType.slice(1);

        operations.push({
          op: 'replace',
          path: `/${capitalizedScheduleType}/stage`,
          value: updatedStage,
        });
      }
    }
  });

  // Processar outras propriedades (excluindo finalAttendanceDocList e scheduleTypes com stage)
  const excludedProps = ['finalAttendanceDocList', ...scheduleTypes];
  const filteredOriginal = { ...original };
  const filteredUpdated = { ...updated };

  // Remover propriedades que já foram processadas
  excludedProps.forEach((prop) => {
    delete filteredOriginal[prop];
    delete filteredUpdated[prop];
  });

  // Para scheduleTypes, processar outras propriedades que não sejam stage
  scheduleTypes.forEach((scheduleType) => {
    if (original[scheduleType] && updated[scheduleType]) {
      const { stage: origStage, ...restOriginal } = original[scheduleType];
      const { stage: updStage, ...restUpdated } = updated[scheduleType];

      // Se há outras mudanças além do stage
      const otherChanges = generatePatchOperations(restOriginal, restUpdated);
      if (otherChanges.length > 0) {
        const capitalizedScheduleType =
          scheduleType.charAt(0).toUpperCase() + scheduleType.slice(1);
        const formattedChanges = otherChanges.map((op) => ({
          ...op,
          path: `/${capitalizedScheduleType}${op.path}`,
        }));
        operations.push(...formattedChanges);
      }
    }
  });

  // Processar outras propriedades normalmente
  const otherOps = generatePatchOperations(filteredOriginal, filteredUpdated);

  // Capitalizar primeira letra do path para outras propriedades
  const formattedOtherOps = otherOps.map((op) => {
    const pathSegments = op.path.split('/');
    if (pathSegments.length > 1 && pathSegments[1]) {
      pathSegments[1] = pathSegments[1].charAt(0).toUpperCase() + pathSegments[1].slice(1);
    }
    return { ...op, path: pathSegments.join('/') };
  });

  operations.push(...formattedOtherOps);
  return operations;
};
